import { trim, isEmpty, uniq } from "lodash";
import { ConsultantPipelineCardFragment } from "@graphql";
import { ConsultantFilterState } from "@consultants/consultants-search-new/utils/consultant-filter-state";
import { decodeGenericAOE } from "~/common/ConsultantSearch";
import { BoardDescription } from "../kanban-board/kanban-types";

export function applyFilter(
  filter: ConsultantFilterState,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  const filters = [
    applyTextSearch,
    applyTagsSearch,
    applyQualificationSearch,
    applyNationalQualificationSearch,
    applyProfessionSearch,
    applyAvailabilityRegions
  ];

  return filters.reduce(
    (newState, filterFunction) => filterFunction(filter, newState),
    state
  );
}

function applyTextSearch(
  filter: ConsultantFilterState,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  const text = trim(filter.text).toLowerCase();
  if (!text) return state;

  return filterColumns(state, (card) => card.title.toLowerCase().includes(text));
}

function applyTagsSearch(
  filter: ConsultantFilterState,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  if (isEmpty(filter.tags)) return state;

  return filterColumns(state, (card) => {
    if (filter.tags.length === 1 && filter.tags[0].value === null) {
      return !card.issue?.consultant?.tags || isEmpty(card.issue?.consultant?.tags);
    }

    return filter.tags.some((tag) => card.issue?.consultant?.tags?.includes(tag.value));
  });
}

function applyQualificationSearch(
  filter: ConsultantFilterState,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  if (!filter.qualification) return state;

  const qualificationMap = {
    all: () => true,
    showQualified: (card) => card.issue?.consultant?.isQualityApproved === true,
    showExpired: (card) => card.issue?.consultant?.isQualityExpired === true,
    showUnqualified: (card) => card.issue?.consultant?.isQualityApproved === false
  };

  return filterColumns(state, qualificationMap[filter.qualification] || (() => false));
}

function applyNationalQualificationSearch(
  filter: ConsultantFilterState,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  if (!filter.nationalQualification) return state;

  const nationalQualificationMap = {
    all: () => true,
    presentationReady: (card) =>
      card.issue?.consultant?.nationalQualification === "PresentationReady",
    showQualified: (card) =>
      card.issue?.consultant?.nationalQualification === "Qualified",
    showExpired: (card) => card.issue?.consultant?.nationalQualification === "Expired",
    showUnqualified: (card) =>
      card.issue?.consultant?.nationalQualification === "Unqualified"
  };

  return filterColumns(
    state,
    nationalQualificationMap[filter.nationalQualification] || (() => false)
  );
}

function applyProfessionSearch(
  filter: ConsultantFilterState,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  if (isEmpty(filter.simpleProfession.professions)) return state;

  return filterColumns(state, (card) => {
    const aoes = filter.simpleProfession.areaOfExpertises
      .map(decodeGenericAOE)
      .map((x) => x[1]);

    if (filter.simpleProfession.professions.length === 1 && aoes.length) {
      return (
        filter.simpleProfession.professions.every((profession) =>
          card.issue?.consultant?.Professions?.includes(profession)
        ) && aoes.some((aoe) => card.issue?.consultant?.AreasOfExpertise?.includes(aoe))
      );
    }

    return filter.simpleProfession.professions.some((profession) =>
      card.issue?.consultant?.Professions?.includes(profession)
    );
  });
}

function applyAvailabilityRegions(
  filter: ConsultantFilterState,
  state: BoardDescription<ConsultantPipelineCardFragment>
): BoardDescription<ConsultantPipelineCardFragment> {
  if (isEmpty(filter.regions)) return state;

  return filterColumns(state, (card) => {
    const regionsToSearch = [];

    for (const region of filter.regions) {
      const country = region.split(":")[0];
      regionsToSearch.push(`${country}:*`);
      regionsToSearch.push(region);

      // todo: migrate all "Bonliva Care" values to "Bonliva Care:*" and remove this hack
      if (region.startsWith("Bonliva Care")) {
        regionsToSearch.push("Bonliva Care:*");
        regionsToSearch.push("Bonliva Care");
      }
      // end of hack
    }

    return uniq(regionsToSearch).some((region) =>
      card.issue?.consultant?.generalInterest?.regions?.includes(region)
    );
  });
}

function filterColumns(
  state: BoardDescription<ConsultantPipelineCardFragment>,
  filterFunction: (card: any) => boolean
): BoardDescription<ConsultantPipelineCardFragment> {
  const filteredColumns = state.columns.map((column) => ({
    ...column,
    cards: column.cards.filter(filterFunction)
  }));

  return {
    ...state,
    columns: filteredColumns
  };
}
